import { FC, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Search as SearchIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import MainTitle from "../../components/mainTitle";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { useHelpContext } from "../../../context/help.context";
import { helpContentMap } from "../../../utils/helpContent";
import HelpIconComponent from "../../components/help/HelpIcon";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { HelpContent } from "../../../context/help.context";

/** Fuzzy match: query chars must appear in order in text (case-insensitive) */
const fuzzyMatch = (query: string, text: string): boolean => {
  if (!query.trim()) return true;
  const q = query.toLowerCase().trim();
  const t = text.toLowerCase();
  let j = 0;
  for (let i = 0; i < t.length && j < q.length; i++) {
    if (t[i] === q[j]) j++;
  }
  return j === q.length;
};

const getSearchableText = (screenId: string, content: HelpContent): string => {
  const parts = [
    screenId,
    content.title,
    content.description,
    content.indianContext ?? "",
  ];
  if (content.fields) {
    Object.values(content.fields).forEach((f) => {
      parts.push(f.label, f.description, f.indianContext ?? "");
    });
  }
  return parts.join(" ");
};

const HelpView: FC = () => {
  const { isHelpEnabled, toggleHelp, showHelp } = useHelpContext();
  const { control, watch } = useForm<{ searchQuery: string }>({
    defaultValues: { searchQuery: "" },
  });
  const searchQuery = watch("searchQuery");

  const filteredEntries = useMemo(() => {
    return Object.entries(helpContentMap).filter(([screenId, content]) =>
      fuzzyMatch(searchQuery ?? "", getSearchableText(screenId, content)),
    );
  }, [searchQuery]);

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.help || "Help & Documentation"} />

      <Grid container spacing={3} data-testid="help-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            data-testid="help-header"
          >
            <MainTitle title="Help & Documentation" />
            <FormControlLabel
              control={
                <Switch
                  checked={isHelpEnabled}
                  onChange={toggleHelp}
                  data-testid="help-toggle-switch"
                />
              }
              label="Enable Help Tooltips"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="help-welcome-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Welcome to Varisht Admin Help
              </Typography>
              <Typography variant="body1" paragraph>
                This help system provides contextual guidance for configuring
                and managing educational institutes. You can find help icons
                throughout the interface that provide specific guidance for each
                screen and field.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Help content includes information specific to Indian educational
                system requirements, including CBSE, ICSE, and State Board
                compliance.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Screen-Specific Help
            </Typography>
            <Box sx={{ flex: 1, minWidth: 280, maxWidth: 400 }}>
              <ControlledTextField
                control={control as any}
                name="searchQuery"
                id="help-search"
                label="Search help"
                placeholder="e.g. fee, compliance, institute..."
                isGridItem={false}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Grid>

        {filteredEntries.length === 0 ? (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  No help content matches &quot;{searchQuery}&quot;. Try a different search.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
        filteredEntries.map(([screenId, content]) => (
          <Grid item xs={12} key={screenId}>
            <Accordion data-testid={`help-accordion-${screenId}`}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  pr={2}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    {content.title}
                  </Typography>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      showHelp(screenId);
                    }}
                    sx={{ ml: 2 }}
                    data-testid={`view-help-button-${screenId}`}
                  >
                    View Help
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  {content.description}
                </Typography>
                {content.indianContext && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      fontWeight="bold"
                    >
                      Indian Educational Context
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      {content.indianContext}
                    </Typography>
                  </>
                )}
                {content.fields && Object.keys(content.fields).length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      fontWeight="bold"
                    >
                      Field Help
                    </Typography>
                    {Object.entries(content.fields).map(
                      ([fieldId, fieldInfo]) => (
                        <Box key={fieldId} sx={{ mb: 2 }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={0.5}
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {fieldInfo.label}
                            </Typography>
                            <HelpIconComponent
                              screenId={screenId}
                              fieldId={fieldId}
                              size="small"
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            paragraph
                          >
                            {fieldInfo.description}
                          </Typography>
                          {fieldInfo.indianContext && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 0.5, fontStyle: "italic" }}
                            >
                              <strong>Note:</strong> {fieldInfo.indianContext}
                            </Typography>
                          )}
                        </Box>
                      ),
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))
        )}

        <Grid item xs={12}>
          <Card data-testid="help-usage-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                How to Use Help
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" paragraph>
                  Look for help icons next to form fields and on screens
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  Click help icons to view contextual help for that specific
                  field or screen
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  Use the toggle switch above to enable or disable help tooltips
                  throughout the interface
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  Help content includes Indian educational system context where
                  relevant
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default HelpView;
